// eslint-disable-next-line @eslint-community/eslint-comments/disable-enable-pair
/* eslint-disable camelcase */
import classnames from 'classnames/bind';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useState } from 'react';
import { Text } from '@portals/ui-kit';
import dynamic from 'next/dynamic';
import {
  EBlockName,
  EButtonName,
  ESlideId,
  TSlide,
} from '@/_components/promo-zone/_types';
import { EAnalytics } from '@/_constants/analytics';
import { useAnalytics } from '@/_hooks/use-analytics';
import { BasketImage } from '@/_components/basket-image';
import { SlideObserver } from './_components/slide-observer';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Slide';

const ClothesDiscountModal = dynamic(
  () =>
    import('./_components/clothes-discount-modal').then((module) => ({
      default: module.ClothesDiscountModal,
    })),
  {
    ssr: false,
  },
);

type TProps = TSlide & { blockName: EBlockName; isIntersectingSlider: boolean };

export const Slide = memo(function SlideComponent({
  title,
  subtitle,
  description,
  image,
  buttonText,
  modal,
  blockName,
  id,
  isIntersectingSlider,
}: TProps) {
  const translations = useTranslations();
  const sendEvent = useAnalytics();

  const [isModalOpened, setIsModalOpened] = useState(false);

  const shouldShowClothesDiscountModal =
    id === ESlideId.CLOTHES_DISCOUNT_WORK && modal;

  const handleButtonClick = useCallback(() => {
    const config = {
      name: EAnalytics.SUPPLIER_LANDING_PROMO_T,
      params: {
        button_name: EButtonName.DISCOUNT_WORK,
        name_block: blockName,
        name_subblock: id,
      },
    };

    sendEvent(config);

    setIsModalOpened(true);
  }, [blockName, id, sendEvent]);

  const handleModalClose = useCallback(() => {
    setIsModalOpened(false);
  }, []);

  return (
    <div className={cn(BLOCK_NAME)}>
      <div className={cn(`${BLOCK_NAME}__content`)}>
        <div className={cn(`${BLOCK_NAME}__titles`)}>
          <div>
            <Text color="white" size="headline-s" text={translations(title)} />
          </div>
          {subtitle && (
            <Text
              color="white"
              size="headline-s"
              text={translations(subtitle)}
            />
          )}
        </div>
        {description && (
          <div className={cn(`${BLOCK_NAME}__description`)}>
            <Text
              color="white"
              size="body-l"
              text={translations(description)}
            />
          </div>
        )}
        {buttonText && (
          <div className={cn(`${BLOCK_NAME}__button-wrapper`)}>
            <button
              className={cn(`${BLOCK_NAME}__button`)}
              onClick={handleButtonClick}
              type="button"
            >
              <Text
                color="white"
                size="body-s"
                text={translations(buttonText)}
              />
            </button>
          </div>
        )}
      </div>
      {image && (
        <div className={cn(`${BLOCK_NAME}__image`)}>
          <BasketImage alt="background" fill sizes="100%" src={image} />
        </div>
      )}
      {shouldShowClothesDiscountModal && (
        <ClothesDiscountModal
          {...modal}
          isOpen={isModalOpened}
          onClose={handleModalClose}
        />
      )}
      <div className={cn(`${BLOCK_NAME}__observer`)}>
        <SlideObserver
          blockName={blockName}
          isIntersectingSlider={isIntersectingSlider}
          slideId={id}
        />
      </div>
    </div>
  );
});
